/**
 * Shared player-overrides store.
 *
 * Historically `overrides` was pure in-memory state on the Optimizer page,
 * so any component outside that page (Pivot Board, Slate Intel widgets)
 * had no way to write locks / excludes / boosts. This module persists the
 * override map in localStorage and broadcasts changes via a CustomEvent
 * so every mounted component stays in sync.
 *
 * Shape: `{ [dk_id]: { lock, exclude, boost_pct, proj_override,
 *                      min_exposure_pct, max_exposure_pct, dual_threat } }`
 *
 * Empty override objects are pruned automatically; setting a field to
 * `null` / `undefined` / `""` / `false` removes it.
 */
const LEGACY_KEY = "wp_player_overrides";
const SLATE_KEY = "wp_overrides_slate";
const CHANGED_EVENT = "wp:overrides-changed";

// Overrides are scoped per DK draft group: dk_id is DK's stable playerId, so an
// unscoped map let a Min-Proj purge from one slate/week ban the same players on
// every later slate ("532 MY EXCLUDE" the user never clicked).
function storageKey() {
  const dg = typeof localStorage !== "undefined" ? localStorage.getItem(SLATE_KEY) : null;
  return dg ? `${LEGACY_KEY}:${dg}` : LEGACY_KEY;
}

/** Called whenever the active slate is known; switching slates swaps the map. */
export function setActiveSlateKey(dg) {
  if (typeof localStorage === "undefined") return;
  const next = dg === null || dg === undefined || dg === "" ? "" : String(dg);
  const cur = localStorage.getItem(SLATE_KEY) || "";
  if (next === cur) return;
  if (!next) return;
  localStorage.setItem(SLATE_KEY, next);
  if (!cur) localStorage.removeItem(LEGACY_KEY);
  window.dispatchEvent(new CustomEvent(CHANGED_EVENT, { detail: { slate: next } }));
}

export function loadOverrides() {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(storageKey());
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === "object") ? parsed : {};
  } catch {
    return {};
  }
}

export function saveOverrides(next) {
  if (typeof localStorage === "undefined") return;
  try {
    const clean = next && typeof next === "object" ? next : {};
    localStorage.setItem(storageKey(), JSON.stringify(clean));
    window.dispatchEvent(new CustomEvent(CHANGED_EVENT, { detail: { size: Object.keys(clean).length } }));
  } catch { /* private mode safe */ }
}

function _clean(ov) {
  return Object.fromEntries(Object.entries(ov).filter(([, v]) =>
    v !== null && v !== undefined && v !== "" && v !== false,
  ));
}

/** Apply a patch to a single player's override entry. Pass `null` for a
 *  key to remove it. Returns the new full overrides map. */
export function patchPlayerOverride(dkId, patch) {
  const key = String(dkId || "");
  if (!key) return loadOverrides();
  const cur = loadOverrides();
  const merged = { ...(cur[key] || {}), ...patch };
  const cleaned = _clean(merged);
  const next = { ...cur };
  if (Object.keys(cleaned).length === 0) delete next[key];
  else next[key] = cleaned;
  saveOverrides(next);
  return next;
}

/** Apply patches to multiple players in one write. `patches`:
 *  `[{ dkId, patch: { lock: true, ... } }, ...]` */
export function patchOverrides(patches) {
  const cur = loadOverrides();
  const next = { ...cur };
  for (const { dkId, patch } of patches || []) {
    const k = String(dkId || "");
    if (!k) continue;
    const merged = { ...(next[k] || {}), ...patch };
    const cleaned = _clean(merged);
    if (Object.keys(cleaned).length === 0) delete next[k];
    else next[k] = cleaned;
  }
  saveOverrides(next);
  return next;
}

/** Remove all fields for a single player. */
export function clearPlayerOverride(dkId) {
  const key = String(dkId || "");
  if (!key) return loadOverrides();
  const cur = loadOverrides();
  if (!(key in cur)) return cur;
  const next = { ...cur };
  delete next[key];
  saveOverrides(next);
  return next;
}

export const OVERRIDES_CHANGED_EVENT = CHANGED_EVENT;
